/**
 * ACTIVITY-CORE — pure presentation helpers for the feed (UI-agnostic).
 * Human-readable text (i18n) lives in the app; this module only handles
 * structure: patch parsing, batch grouping and "done offline" detection.
 */

/** activity_recent row as it arrives from local SQLite. */
export interface ActivityRow {
  id: string;
  scope_id: string | null;
  list_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  action: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_device_id: string | null;
  platform: string | null;
  server_ts: string | null;
  client_ts: string | null;
  patch: string | null;
  batch_id: string | null;
  reverts_activity_id: string | null;
}

export type ActivityAction = "created" | "updated" | "deleted" | "restored" | "reverted";

export interface ActivityFieldChange {
  old: unknown;
  new: unknown;
}

export type ActivityPatch = Record<string, ActivityFieldChange>;

/** Type guard for a `{old, new}` change (no unsafe assertions). */
function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Parses the jsonb patch (it arrives as TEXT in SQLite). Never throws. */
export function parseActivityPatch(raw: string | null | undefined): ActivityPatch {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const patch: ActivityPatch = {};
    for (const [field, change] of Object.entries(parsed)) {
      if (isFieldChange(change)) {
        patch[field] = { old: change.old, new: change.new };
      }
    }
    return patch;
  } catch {
    return {};
  }
}

/** Changed fields of an entry, in stable order. */
export function changedFields(patch: ActivityPatch): string[] {
  return Object.keys(patch).toSorted();
}

/**
 * Threshold from which a client_ts↔server_ts difference is considered
 * "made offline and synced later" (rather than mere network latency).
 */
export const OFFLINE_LAG_THRESHOLD_MS = 60_000;

/**
 * Milliseconds between when the client made the change (client_ts)
 * and its arrival at the server (server_ts). `null` if either is missing or parsing fails.
 */
export function activityLagMs(row: Pick<ActivityRow, "server_ts" | "client_ts">): number | null {
  if (!row.server_ts || !row.client_ts) {
    return null;
  }
  const server = Date.parse(row.server_ts);
  const client = Date.parse(row.client_ts);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return null;
  }
  return server - client;
}

/** Was it done offline? (the client stamped it long before it reached the server) */
export function isOfflineEdit(
  row: Pick<ActivityRow, "server_ts" | "client_ts">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  const lag = activityLagMs(row);
  return lag !== null && lag > thresholdMs;
}

/**
 * Entities whose history the server knows how to compensate (mirror of
 * `tracked_fields` in `core-rust/models/activity.rs`). Outside this set there is no revert.
 */
const REVERTIBLE_ENTITY_TYPES = new Set<string>(["journal_entry", "journal_tag"]);

/**
 * Can this feed entry be undone from the UI? Reflects the backend gating
 * (`use_cases::activity::revert` + `build_compensating_op`):
 *
 *  - the entity must be tracked (`REVERTIBLE_ENTITY_TYPES`);
 *  - `created` can always be undone (compensated with delete/soft-delete);
 *  - `updated` / `deleted` / `restored` need a non-empty patch from which to
 *    obtain the previous values (without a patch there is nothing to reverse);
 *  - a `reverted` (or unknown action) is NOT offered: it avoids the confusing
 *    "redo" chained from the feed itself.
 *
 * This is an honest pre-filter so we do not show a button the server would reject;
 * the endpoint remains the final authority (it can return 409 if the change
 * no longer applies).
 */
export function isRevertibleActivity(
  row: Pick<ActivityRow, "entity_type" | "action" | "patch">,
): boolean {
  if (!row.entity_type || !REVERTIBLE_ENTITY_TYPES.has(row.entity_type)) {
    return false;
  }
  if (row.action === "created") {
    return true;
  }
  if (row.action === "updated" || row.action === "deleted" || row.action === "restored") {
    return Object.keys(parseActivityPatch(row.patch)).length > 0;
  }
  // `reverted` or an unknown/null action: not offered (avoids the confusing redo).
  return false;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  /** Common batch_id (or the row id if it has no batch). */
  batchId: string;
  entries: T[];
}

/**
 * Groups CONSECUTIVE feed rows (already sorted by server_ts DESC) by batch_id:
 * "imported 40 tasks" is rendered as one group, not 40 cards. Does not reorder.
 */
export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batch_id ?? row.id;
    const last = groups.at(-1);
    if (last?.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}
